/**
 * Static data + lookup for the Game Pass → Configuration → Monthly → Details
 * page (FE-2275, "Scheduled Monthly Challenges view").
 *
 * Monthly Challenges have no admin API yet, so — exactly like the Monthly
 * listing (`monthlyConfigurationData`) and the create wizard — the details view
 * is driven by placeholder records kept in this self-contained module. When the
 * backend contract lands, a single mapper swaps these mocks for API data while
 * every consumer keeps rendering the same shapes.
 *
 * A monthly challenge's per-challenge shape is the wizard's `MonthlyChallenge
 * Draft`, reused verbatim so the details view, the wizard and any future editor
 * never drift on what a challenge is.
 */
import type { SeasonCardStatusTone } from "../../SeasonCard/SeasonCard";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  DEFAULT_TIER_REWARD_TYPE,
  type MonthlyChallengeDraft,
  type MonthlyTaskType,
  type MonthlyTierDraft,
  type RewardType,
} from "../monthlyChallengeData";

/** General (header) card content for a monthly challenge details page. */
export type MonthlyDetailsGeneral = {
  /** Display name (e.g. "June Monthly"). */
  name: string;
  /** Lifecycle status pill — label + tone drive the colour. */
  status: { label: string; tone: SeasonCardStatusTone };
  /** Optional "(Locks in 64 days)" countdown shown after the status. */
  countdown?: { prefix: string; value: string };
  /** Pre-formatted full date range string. */
  dateRange: string;
  /** Locked-view audience metrics (Active monthly only). Enrolled players +
   *  average completion drive the two number tiles; `reportsCount` is carried
   *  for parity with Seasons but the tile only surfaces a "See all" link. */
  enrolled?: number;
  avgCompletionPct?: number;
  reportsCount?: number;
  /** Whether the destructive "End Monthly Challenge" link shows (Active only). */
  endEnabled?: boolean;
  /** Whether the "Unschedule" link shows (Scheduled only) — pulls the campaign
   *  back to draft via the `/unschedule` transition. */
  unscheduleEnabled?: boolean;
  /** Whether this campaign is still a Draft. Drives the edit popup's primary
   *  action for the mock deep-link records (which carry no API status): a Draft
   *  reads "Schedule" and promotes out of `draft` on Save, a Scheduled campaign
   *  reads "Save" and re-times in place. */
  scheduleEnabled?: boolean;
};

export type MonthlyDetailsData = {
  id: string;
  /** `editable` → Scheduled/Draft in-place editor (FE-2275). `locked` →
   *  read-only Active view (FE-2276): challenges render via
   *  `MonthlyChallengeCard mode="locked"` and the General card gains the
   *  audience metric tiles, mirroring how Seasons handles an Active season. */
  mode: "locked" | "editable";
  general: MonthlyDetailsGeneral;
  challenges: ReadonlyArray<MonthlyChallengeDraft>;
};

/** Id of the Scheduled monthly challenge surfaced on the listing's Next Month
 *  column. Shared with `monthlyConfigurationData` so the card the operator
 *  clicks and the detail record it opens stay in lockstep. */
export const MONTHLY_DETAILS_SCHEDULED_ID = "mock-june-monthly-scheduled";

/** Id of the Active monthly challenge surfaced on the listing's Active column.
 *  Matches `monthlyConfigurationData`'s `MOCK_ACTIVE_MONTHLY.id` so the locked
 *  card the operator opens resolves to this read-only record. */
export const MONTHLY_DETAILS_ACTIVE_ID = "mock-june-monthly";

type TierSeed = {
  threshold: string;
  rewardAmount: string;
  rewardType?: RewardType;
  xpReward?: string;
};

const buildTier = (
  challengeId: string,
  index: number,
  seed: TierSeed,
): MonthlyTierDraft => ({
  id: `${challengeId}-tier-${index}`,
  threshold: seed.threshold,
  rewardAmount: seed.rewardAmount,
  rewardType: seed.rewardType ?? DEFAULT_TIER_REWARD_TYPE,
  xpReward: seed.xpReward ?? "",
});

type ChallengeSeed = {
  id: string;
  name: string;
  description: string;
  taskType?: MonthlyTaskType;
  tiers: ReadonlyArray<TierSeed>;
  /** Which challenge opens expanded to its Tier Details on first paint. */
  expanded?: boolean;
};

const buildChallenge = (seed: ChallengeSeed): MonthlyChallengeDraft => ({
  id: seed.id,
  name: seed.name,
  description: seed.description,
  taskType: seed.taskType ?? "FOLLOW_FOR_FOLLOW",
  pickType: "ANY",
  minimumOdds: "",
  casinoProviders: [],
  casinoGames: [],
  casinoGameLabels: {},
  casinoAction: "PLAY",
  tiers: seed.tiers.map((tier, index) => buildTier(seed.id, index, tier)),
  isExpanded: Boolean(seed.expanded),
  // These are persisted (scheduled) challenges, not in-flight drafts.
  isSaved: true,
});

const FOLLOW_TIERS: ReadonlyArray<TierSeed> = [
  { threshold: "10", rewardAmount: "50", xpReward: "25" },
  { threshold: "20", rewardAmount: "50", xpReward: "50" },
  { threshold: "30", rewardAmount: "50", xpReward: "100" },
];

/** The Scheduled "June Monthly" challenge from the FE-2275 Figma. */
const SCHEDULED_MONTHLY_DETAILS: MonthlyDetailsData = {
  id: MONTHLY_DETAILS_SCHEDULED_ID,
  mode: "editable",
  general: {
    name: "June Monthly",
    status: { label: AppStrings.SEASONS_FILTER_SCHEDULED, tone: "upNext" },
    countdown: {
      prefix: AppStrings.SEASON_COUNTDOWN_LOCKS_IN,
      value: "64 days",
    },
    dateRange: "June 1, 2026 (12:00am) - June 30, 2026 (11:59pm)",
    unscheduleEnabled: true,
  },
  challenges: [
    buildChallenge({
      id: "follower-challenge",
      name: "Follower Challenge",
      description: "Gain 100 followers",
      tiers: FOLLOW_TIERS,
    }),
    buildChallenge({
      id: "parlay-challenge",
      name: "Parlay Challenge",
      description: "Gain 30 followers",
      tiers: FOLLOW_TIERS,
      expanded: true,
    }),
    buildChallenge({
      id: "free-bet-challenge",
      name: "Free Bet Challenge",
      description: "Gain 100 followers",
      tiers: FOLLOW_TIERS,
    }),
  ],
};

/** The Active "June Monthly Challenge" from the FE-2276 Figma — locked /
 *  read-only. Same challenge shapes as the Scheduled record, but the page
 *  renders them non-editable and the General card shows audience metrics. */
const ACTIVE_MONTHLY_DETAILS: MonthlyDetailsData = {
  id: MONTHLY_DETAILS_ACTIVE_ID,
  mode: "locked",
  general: {
    name: "June Monthly Challenge",
    status: { label: AppStrings.SEASON_STATUS_ACTIVE, tone: "active" },
    dateRange: "June 1, 2026 (12:00am) - June 30, 2026 (11:59pm)",
    enrolled: 2012,
    avgCompletionPct: 68,
    reportsCount: 12,
    endEnabled: true,
  },
  challenges: [
    buildChallenge({
      id: "active-follower-challenge",
      name: "Follower Challenge",
      description: "Gain 100 followers",
      tiers: FOLLOW_TIERS,
    }),
    buildChallenge({
      id: "active-parlay-challenge",
      name: "Parlay Challenge",
      description: "Gain 30 followers",
      tiers: FOLLOW_TIERS,
      expanded: true,
    }),
    buildChallenge({
      id: "active-free-bet-challenge",
      name: "Free Bet Challenge",
      description: "Gain 100 followers",
      tiers: FOLLOW_TIERS,
    }),
  ],
};

const MONTHLY_DETAILS_BY_ID: Readonly<Record<string, MonthlyDetailsData>> = {
  [MONTHLY_DETAILS_SCHEDULED_ID]: SCHEDULED_MONTHLY_DETAILS,
  [MONTHLY_DETAILS_ACTIVE_ID]: ACTIVE_MONTHLY_DETAILS,
};

/**
 * Look up a monthly challenge details record by id. Returns `undefined` for an
 * unknown id so the page can render its not-found state. Deep-clones the
 * challenge list (and every tier) so the page's local edits never mutate the
 * shared mock — the same isolation a real API response would give.
 */
export function findMonthlyDetailsById(
  id: string | undefined,
): MonthlyDetailsData | undefined {
  if (!id) return undefined;
  const record = MONTHLY_DETAILS_BY_ID[id];
  if (!record) return undefined;
  return {
    ...record,
    challenges: record.challenges.map((challenge) => ({
      ...challenge,
      tiers: challenge.tiers.map((tier) => ({ ...tier })),
    })),
  };
}
